import { Box, Button, styled, Typography } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { usePortraitsTranslator } from "../i18n/translator";
import { checkPhoto, PhotoProblem } from "./checkPhoto";
import { FlowPanel, StepProps } from "./FlowPanel";
import { FramedImage, PortraitFrame } from "./PortraitFrame";
import { isUnderWay } from "./usePortrait";

// MUI's file upload pattern: the input stays focusable for keyboard users
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const PhotoStep = ({ portrait, name, flow, headingRef }: StepProps) => {
  const translator = usePortraitsTranslator();
  const { busy, upload, cutOutAgain } = flow;
  const [photoProblem, setPhotoProblem] = useState<PhotoProblem>();
  const [dragging, setDragging] = useState(false);
  const photoUrl = portrait.cutout?.imageUrl;
  const working = upload.isPending || isUnderWay(portrait.cutout);

  const takePhoto = (photo: File | undefined) => {
    if (!photo || busy) {
      return;
    }
    const problem = checkPhoto(photo);
    setPhotoProblem(problem);
    if (!problem) {
      flow.takePhoto(photo);
    }
  };

  const status = upload.isPending
    ? translator("uploading")
    : portrait.cutout?.status === "queued"
      ? translator("jobQueued")
      : portrait.cutout?.status === "processing"
        ? translator("cutoutProcessing")
        : undefined;

  const error = photoProblem
    ? translator(photoProblem)
    : upload.isError
      ? translator("uploadFailed")
      : cutOutAgain.isError
        ? translator("unknownErrorTryAgainLater")
        : portrait.cutout?.status === "failed"
          ? translator("cutoutFailed")
          : undefined;

  return (
    <FlowPanel
      name={name}
      flow={flow}
      headingRef={headingRef}
      stepTitle={translator("photoTitle")}
      hint={translator(photoUrl ? "photoHint" : "photoHintEmpty")}
      picture={
        <PortraitFrame
          busy={working}
          highlighted={dragging}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            takePhoto(event.dataTransfer.files[0]);
          }}
        >
          {photoUrl ? (
            <FramedImage
              url={photoUrl}
              alt={translator("cutoutAlt")}
              dimmed={working}
            />
          ) : (
            <Typography color="text.secondary">
              {translator("noCutoutYet")}
            </Typography>
          )}
          {dragging && (
            <Box
              position="absolute"
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="background.paper"
              sx={{ inset: 0, opacity: 0.9, pointerEvents: "none" }}
            >
              <Typography>{translator("dropPhotoHere")}</Typography>
            </Box>
          )}
        </PortraitFrame>
      }
      status={status}
      error={error}
      actions={
        <>
          <Button
            component="label"
            role={undefined}
            tabIndex={-1}
            variant="contained"
            fullWidth
            disabled={busy}
          >
            {translator(photoUrl ? "uploadNewPhoto" : "uploadPhoto")}
            <VisuallyHiddenInput
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={busy}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const photo = event.target.files?.[0];
                // Lets the same file be picked again after a failure
                event.target.value = "";
                takePhoto(photo);
              }}
            />
          </Button>
          {portrait.hasUpload && (
            <Button
              variant="outlined"
              fullWidth
              disabled={busy}
              onClick={() => {
                setPhotoProblem(undefined);
                flow.redoCutout();
              }}
            >
              {translator("cutOutAgain")}
            </Button>
          )}
        </>
      }
    />
  );
};
